// Tax Report Page (G50 - Algerian TVA Declaration)
import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { FileText, Building2, Calendar, DollarSign } from 'lucide-react';
import { useTaxReport } from '@/features/reports/hooks/useTaxReport';
import { PeriodSelector } from '@/features/reports/components/PeriodSelector';
import { MetricCard } from '@/features/reports/components/MetricCard';
import { ExportButton } from '@/features/reports/components/ExportButton';
import { getThisMonth } from '@/features/reports/utils/periods';
import { formatCurrency } from '@/features/reports/utils/calculations';
import type { ExportFormat } from '@/types/report';

export const Route = createFileRoute('/apps/reports/tax')({  
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: TaxReportPage,
});

function TaxReportPage() {
  const [period, setPeriod] = useState(getThisMonth());
  const { data, isLoading, error } = useTaxReport(period);

  const handleExport = async (format: ExportFormat) => {
    console.log('Exporting G50 tax report as', format);
    await new Promise(resolve => setTimeout(resolve, 1000));
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            Erreur lors du chargement du rapport: {error.message}
          </p>
        </div>
      </div>
    );
  }

  const g50 = data?.g50;
  const tvaToPay = (g50?.tvaToPay || 0) > 0;

  return (
    <DashboardLayout>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            <span className="font-arabic">تقرير الضرائب</span>
            <span className="text-muted-foreground mx-2">/</span>
            <span>Rapport TVA (G50)</span>
          </h1>
          <p className="text-muted-foreground mt-2">
            Déclaration mensuelle de la Taxe sur la Valeur Ajoutée - Série G N°50
          </p>
        </div>
        
        <ExportButton 
          onExport={handleExport} 
          disabled={isLoading || !data}
          formats={['pdf', 'excel']}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Period Selector */}
        <div className="lg:col-span-1">
          <PeriodSelector value={period} onChange={setPeriod} />
        </div>

        {/* Key Metrics */}
        <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          <MetricCard
            title="TVA collectée"
            titleAr="الضريبة المحصلة"
            value={g50?.tvaCollectee || 0}
            format="currency"
            icon={DollarSign}
            color="green"
            subtitle="19% sur ventes"
          />
          
          <MetricCard
            title="TVA déductible"
            titleAr="الضريبة القابلة للخصم"
            value={g50?.tvaDeductible || 0}
            format="currency"
            icon={FileText}
            color="blue"
            subtitle="19% sur achats"
          />
          
          <MetricCard
            title={tvaToPay ? "TVA à payer" : "Crédit de TVA"}
            titleAr={tvaToPay ? "الضريبة المستحقة" : "رصيد الضريبة"}
            value={tvaToPay ? (g50?.tvaToPay || 0) : (g50?.tvaCredit || 0)}
            format="currency"
            icon={Building2}
            color={tvaToPay ? "red" : "green"}
            subtitle={tvaToPay ? "À verser à la DGI" : "En votre faveur"}
          />
          
          <MetricCard
            title="Chiffre d'affaires HT"
            titleAr="رقم الأعمال"
            value={g50?.salesHT || 0}
            format="currency"
            icon={Calendar}
            color="purple"
            subtitle="Base imposable"
          />
        </div>
      </div>

      {/* G50 Form */}
      {isLoading ? (
        <div className="h-96 bg-card border border-border rounded-lg animate-pulse" />
      ) : g50 ? (
        <div className="bg-card border border-border rounded-lg p-8">
          {/* Header */}
          <div className="border-b border-border pb-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  RÉPUBLIQUE ALGÉRIENNE DÉMOCRATIQUE ET POPULAIRE
                </h2>
                <h3 className="text-xl font-semibold text-foreground">
                  SÉRIE G N° 50
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  DÉCLARATION DE LA TAXE SUR LA VALEUR AJOUTÉE
                </p>
              </div>
              <div className="w-24 h-24 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                <FileText className="w-12 h-12 text-white" />
              </div>
            </div>
          </div>

          {/* Company Info */}
          <div className="grid grid-cols-2 gap-6 mb-8 p-4 bg-background rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground">Raison sociale</p>
              <p className="font-semibold text-foreground">{g50.company.name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">NIF</p>
              <p className="font-semibold text-foreground">{g50.company.nif}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Adresse</p>
              <p className="font-semibold text-foreground">{g50.company.address}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Wilaya</p>
              <p className="font-semibold text-foreground">{g50.company.wilaya}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Période</p>
              <p className="font-semibold text-foreground">
                {new Date(g50.period.year, g50.period.month - 1).toLocaleDateString('fr-DZ', { month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>

          {/* Section A: Chiffre d'affaires */}
          <div className="mb-8">
            <h4 className="text-lg font-bold text-foreground mb-4 pb-2 border-b border-border">
              A. CHIFFRE D'AFFAIRES
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Ventes HT</p>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(g50.salesHT, false)}
                </p>
              </div>
              <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">TVA 19%</p>
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(g50.tvaCollectee, false)}
                </p>
              </div>
              <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Total TTC</p>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(g50.salesTTC, false)}
                </p>
              </div>
            </div>
          </div>

          {/* Section B: Achats */}
          <div className="mb-8">
            <h4 className="text-lg font-bold text-foreground mb-4 pb-2 border-b border-border">
              B. ACHATS ET PRESTATIONS
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Achats HT</p>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(g50.purchasesHT, false)}
                </p>
              </div>
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">TVA 19%</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {formatCurrency(g50.tvaDeductible, false)}
                </p>
              </div>
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Total TTC</p>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(g50.purchasesTTC, false)}
                </p>
              </div>
            </div>
          </div>

          {/* Section C: Liquidation */}
          <div className="mb-8">
            <h4 className="text-lg font-bold text-foreground mb-4 pb-2 border-b border-border">
              C. LIQUIDATION DE LA TVA
            </h4>
            <div className="p-6 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">
                  TVA collectée - TVA déductible
                </p>
                <p className="text-sm text-muted-foreground">
                  {formatCurrency(g50.tvaCollectee, false)} - {formatCurrency(g50.tvaDeductible, false)}
                </p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-lg font-bold text-foreground">
                  {tvaToPay ? "TVA À PAYER" : "CRÉDIT DE TVA"}
                </p>
                <p className={`text-3xl font-bold ${tvaToPay ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                  {formatCurrency(tvaToPay ? g50.tvaToPay : g50.tvaCredit)}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="pt-6 border-t border-border">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <p>Date limite de dépôt: 20 {new Date(g50.period.year, g50.period.month).toLocaleDateString('fr-DZ', { month: 'long', year: 'numeric' })}</p>
              <p>À soumettre via CNIS (www.mfdgi.gov.dz)</p>
            </div>
          </div>
        </div>
      ) : null}

      {/* Monthly Breakdown */}
      {data && data.monthlyBreakdown.length > 1 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            <span className="font-arabic">التفصيل الشهري</span>
            <span className="text-muted-foreground mx-2">/</span>
            <span>Détail mensuel</span>
          </h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Mois</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">CA HT</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">TVA Collectée</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Achats HT</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">TVA Déductible</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">TVA Nette</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.monthlyBreakdown.map((month) => {
                  const isPaying = month.netTVA > 0;
                  return (
                    <tr key={month.month} className="hover:bg-accent">
                      <td className="px-4 py-3 text-sm text-foreground">
                        {new Date(month.month + '-01').toLocaleDateString('fr-DZ', { month: 'long', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-foreground">
                        {formatCurrency(month.salesHT)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(month.tvaCollectee)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-foreground">
                        {formatCurrency(month.purchasesHT)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-blue-600 dark:text-blue-400">
                        {formatCurrency(month.tvaDeductible)}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-semibold ${isPaying ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                        {formatCurrency(Math.abs(month.netTVA))}
                        {isPaying ? ' à payer' : ' crédit'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </div>
    </DashboardLayout>
  );
}
